import React from "react";
import { formatValue } from '@/lib/post_analysis_utils/formatValue';

const getDeviationColor = (value, metricType) => {
  if (value === undefined || value === null) return "text-gray-500";
  
  // For CPM (currency type), lower is better
  if (metricType === 'currency') {
    if (value <= -2) return "text-green-600 font-medium";
    if (value <= -1) return "text-green-500";
    if (value <= 0) return "text-yellow-600";
    return "text-red-600";
  }
  
  // For engagement rate (percent type), higher is better
  if (metricType === 'percent') {
    if (value >= 2) return "text-green-600 font-medium";
    if (value >= 1) return "text-green-500";
    if (value >= 0) return "text-yellow-600";
    return "text-red-600";
  }
  
  // For all other metrics (numbers), higher is better
  if (value >= 2) return "text-green-600 font-medium";
  if (value >= 1) return "text-green-500";
  if (value >= 0) return "text-yellow-600";
  return "text-red-600";
};

const getDeviationLabel = (value, metricType) => {
  if (value === undefined || value === null) return "N/A";
  
  // For CPM (currency type), lower is better
  if (metricType === 'currency') {
    if (value <= -2) return "Very Good";
    if (value <= -1) return "Good";
    if (value <= 0) return "Average";
    return "Poor";
  }
  
  // For engagement rate (percent type), higher is better
  if (metricType === 'percent') {
    if (value >= 2) return "Very Good";
    if (value >= 1) return "Good";
    if (value >= 0) return "Average";
    return "Poor";
  }
  
  // For all other metrics (numbers), higher is better
  if (value >= 2) return "Very Good";
  if (value >= 1) return "Good";
  if (value >= 0) return "Average";
  return "Poor";
};

export default function BenchmarkDeviationTable({ data, metricTypes = {} }) {
  const formatCellValue = (value, type, metricKey) => {
    if (value == null) return '-';
    // Map metric keys to header names for proper formatting
    const headerMap = {
      'impressions': 'Total Impressions',
      'reach': 'Total Reach',
      'post_engagement': 'Total Engagements',
      'engagement_rate': 'Average Engagement Rate',
      'clicks': 'Clicks',
      'cpm': 'CPM'
    };
    const header = headerMap[metricKey] || '';
    return formatValue(value, { type, header });
  };

  const formatDeviation = (value) => {
    if (value == null) return '-';
    const sign = value > 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  };

  // Define metric types mapping
  const defaultMetricTypes = {
    impressions: 'number',
    reach: 'number',
    post_engagement: 'number',
    engagement_rate: 'percent',
    clicks: 'number',
    cpm: 'currency'
  };

  // Merge default types with any provided types
  const finalMetricTypes = { ...defaultMetricTypes, ...metricTypes };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="text-left py-3 px-4 font-semibold text-gray-700">POC ID</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700">POC Name</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700">Campaign</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700">Brand</th>
            <th className="text-left py-3 px-4 font-semibold text-gray-700">Region</th>
            <th className="text-right py-3 px-4 font-semibold text-gray-700">Total Impressions</th>
            <th className="text-right py-3 px-4 font-semibold text-gray-700">Total Reach</th>
            <th className="text-right py-3 px-4 font-semibold text-gray-700">Total Engagements</th>
            <th className="text-right py-3 px-4 font-semibold text-gray-700">Average Engagement Rate</th>
            <th className="text-right py-3 px-4 font-semibold text-gray-700">Clicks</th>
            <th className="text-right py-3 px-4 font-semibold text-gray-700">CPM</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {data.map((poc, index) => (
            <tr key={index} className="hover:bg-gray-50 transition-colors">
              <td className="py-3 px-4 font-medium">{poc.poc_id || "—"}</td>
              <td className={`py-3 px-4 ${!poc.poc_name ? 'text-muted-foreground italic' : ''}`}>
                {poc.poc_name || (poc.poc_id ? `Default POC ${poc.poc_id}` : "Default POC")}
              </td>
              <td className="py-3 px-4">{poc.campaign_name || "—"}</td>
              <td className="py-3 px-4">{poc.brand || "—"}</td>
              <td className="py-3 px-4">{poc.region || "—"}</td>
              {['impressions', 'reach', 'post_engagement', 'engagement_rate', 'clicks', 'cpm'].map(metric => {
                const metricType = finalMetricTypes[metric];
                const deviation = poc[`${metric}_deviation`];
                const colorClass = getDeviationColor(deviation, metricType);
                
                return (
                  <td key={metric} className="text-right py-3 px-4">
                    <div className="font-medium">{formatCellValue(poc[metric], metricType, metric)}</div>
                    <div className={`text-xs mt-1 ${colorClass}`}>
                      {formatDeviation(deviation)}
                      <span className="ml-1 opacity-75">({getDeviationLabel(deviation, metricType)})</span>
                    </div>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}